import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useConnect } from 'redux-bundler-hook'

import createLogger from '~/src/Lib/Logging'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'

const displayName = 'Hook/IrrigationChangeNotification'
const logger = createLogger(displayName)

const useIrrigationScheduleChangeNotification = () => {
  const {
    harvests,
    irrigationScheduleChangeNotifications,
    irrigationSchedules,
    phases,
    doAcknowledgeIrrigationScheduleChangeNotification,
    doViewIrrigationScheduleChangeNotification,
    doIrrigationScheduleFetch,
  } = useConnect(
    'selectHarvests',
    'selectIrrigationScheduleChangeNotifications',
    'selectIrrigationSchedules',
    'selectPhases',
    'selectRouteInfo',
    'doAcknowledgeIrrigationScheduleChangeNotification',
    'doViewIrrigationScheduleChangeNotification',
    'doIrrigationScheduleFetch',
  )

  const [acknowledged, setAcknowledged] = useState(new Set())

  const notification = useMemo(() => (
    // Return the latest notification
    Object.values(irrigationScheduleChangeNotifications).filter(
      e => !e.acknowledgementOn && !acknowledged.has(e.id) && e.newPhase
    ).slice(-1)[0]
  ), [irrigationScheduleChangeNotifications, acknowledged])

  const [viewed, setViewed] = useState(new Set())
  const doViewNotification = useCallback(() => {
    if (!viewed.has(notification.id)) {
      logger.debug('Irrigation change notification viewed', { notification })
      doViewIrrigationScheduleChangeNotification(notification)
      setViewed(prev => new Set(prev.add(notification.id)))
    }
  }, [viewed, notification, doViewIrrigationScheduleChangeNotification])

  const doAcknowledgeNotification = useCallback(() => {
    if (!acknowledged.has(notification.id)) {
      logger.debug('Irrigation change notification dismissed', { notification })
      doAcknowledgeIrrigationScheduleChangeNotification(notification)
      setAcknowledged(prev => new Set(prev.add(notification.id)))
    }
  }, [notification, acknowledged, doAcknowledgeIrrigationScheduleChangeNotification])

  const {
    currentIrrigationSchedule: currentScheduleId,
    currentPhase: currentPhaseId,
    newIrrigationSchedule: nextScheduleId,
    newPhase: nextPhaseId,
  } = notification ?? EMPTY_OBJECT

  // Entity lookups
  const currentPhase = phases[currentPhaseId]
  const currentSchedule = irrigationSchedules[currentScheduleId] || EMPTY_OBJECT
  const nextPhase = phases[nextPhaseId]
  const nextSchedule = irrigationSchedules[nextScheduleId]

  const harvest = harvests[nextPhase?.harvestId]

  // fetch future schedule
  useEffect(() => {
    if (notification?.newIrrigationSchedule) {
      doIrrigationScheduleFetch(notification.newIrrigationSchedule)
    }
  }, [doIrrigationScheduleFetch, notification?.newIrrigationSchedule])

  return {
    notification,
    currentPhase,
    currentSchedule,
    harvest,
    nextPhase,
    nextSchedule,
    doAcknowledgeNotification,
    doViewNotification,
  }
}

export default useIrrigationScheduleChangeNotification
